export const REPORT_NOTE_TYPE_META = Object.freeze({
  general: { label: "Algemeen", className: "general" },
  safety: { label: "Veiligheid", className: "safety" },
  quality: { label: "Kwaliteit", className: "quality" },
  blocker: { label: "Blokkade", className: "blocker" },
  weather: { label: "Weer", className: "weather" },
  other: { label: "Overig", className: "other" },
});

export interface ReportNoteView {
  reportNoteId: string | null;
  workAssignmentId: string | null;
  noteType: string;
  noteText: string;
  occurredAt: string | null;
  createdAt: string | null;
  requiresFollowUp: boolean;
}

export interface LinkedReportNoteGroup {
  workAssignmentId: string;
  description: string;
  workPackage: string | null;
  notes: ReportNoteView[];
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function regularExpressionLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function noteTypeMeta(noteType: unknown) {
  const normalized = text(noteType).toLowerCase();
  return REPORT_NOTE_TYPE_META[
    normalized as keyof typeof REPORT_NOTE_TYPE_META
  ] ?? REPORT_NOTE_TYPE_META.other;
}

export function formatNoteTime(value: unknown): string | null {
  const raw = text(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString("nl-NL", {
    timeZone: "Europe/Amsterdam",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function displayWorkAssignmentDescription(
  workPackage: unknown,
  description: unknown,
): string {
  const packageName = text(workPackage);
  const rawDescription = text(description) || "Werkzaamheid";
  if (!packageName) return rawDescription;
  const prefix = new RegExp(
    `^${regularExpressionLiteral(packageName)}\\s*(?:[-:\u2013\u2014]\\s*)`,
    "i",
  );
  const withoutDuplicatePrefix = rawDescription.replace(prefix, "").trim();
  return withoutDuplicatePrefix || rawDescription;
}

export function normalizeReportNotes(report: Record<string, unknown>): ReportNoteView[] {
  return (Array.isArray(report.notes) ? report.notes : [])
    .map(record)
    .filter((note): note is Record<string, unknown> => Boolean(note))
    .map((note) => ({
      reportNoteId: text(note.report_note_id) || null,
      workAssignmentId: text(note.work_assignment_id) || null,
      noteType: text(note.note_type).toLowerCase() || "general",
      noteText: text(note.note_text),
      occurredAt: text(note.occurred_at) || null,
      createdAt: text(note.created_at) || null,
      requiresFollowUp: note.requires_follow_up === true,
    }))
    .filter((note) => note.noteText)
    .sort((left, right) => {
      const leftTime = Date.parse(left.occurredAt || left.createdAt || "");
      const rightTime = Date.parse(right.occurredAt || right.createdAt || "");
      const safeLeft = Number.isFinite(leftTime) ? leftTime : Number.MAX_SAFE_INTEGER;
      const safeRight = Number.isFinite(rightTime) ? rightTime : Number.MAX_SAFE_INTEGER;
      return safeLeft - safeRight;
    });
}

export function legacyNoteParagraphs(report: Record<string, unknown>): string[] {
  const generalNote = text(report.general_note ?? report.general_notes);
  if (!generalNote) return [];
  return generalNote
    .split(/\r?\n\s*\r?\n|\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function groupReportNotes(report: Record<string, unknown>) {
  const assignments = new Map<
    string,
    { description: string; workPackage: string | null }
  >();
  for (
    const rawAssignment of Array.isArray(report.work_assignments) ? report.work_assignments : []
  ) {
    const assignment = record(rawAssignment);
    const id = text(assignment?.work_assignment_id);
    if (!id) continue;
    const workPackage = text(assignment?.work_package) || null;
    assignments.set(id, {
      description: displayWorkAssignmentDescription(
        workPackage,
        assignment?.description,
      ),
      workPackage,
    });
  }

  const linked = new Map<string, LinkedReportNoteGroup>();
  const general: ReportNoteView[] = [];
  for (const note of normalizeReportNotes(report)) {
    if (!note.workAssignmentId) {
      general.push(note);
      continue;
    }
    const assignment = assignments.get(note.workAssignmentId);
    const group = linked.get(note.workAssignmentId) ?? {
      workAssignmentId: note.workAssignmentId,
      description: assignment?.description ?? "Onbekende werkzaamheid",
      workPackage: assignment?.workPackage ?? null,
      notes: [],
    };
    group.notes.push(note);
    linked.set(note.workAssignmentId, group);
  }

  return {
    linked: Array.from(linked.values()),
    general,
    legacyParagraphs: legacyNoteParagraphs(report),
  };
}
